import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { AiReceptionistCallTask } from '../../../../domain/models/AiReceptionist';
import { CompanyConversation, ConversationActivityType, ConversationThreadItem } from '../../../../domain/models/Conversation';
import { CompanyContact, ContactSubscription, ContactTag } from '../../../../domain/models/Contact';
import {
  CONTACTS_LAST_INTERACTION_OPTIONS,
  ContactDisplay,
  ContactsLastInteraction,
  buildContactDisplay,
  matchesLastInteraction,
} from '../../contacts/types/ContactDisplay';

dayjs.locale('fr');

export type ConversationsTab = 'all' | 'calls' | 'sms' | 'receptionist';
export type ConversationsSortBy = 'recent' | 'oldest' | 'unread';
export type ConversationsReadState = 'all' | 'unread' | 'read';

export interface ConversationsFilters {
  tab: ConversationsTab;
  search: string;
  subscriptionId: string | null;
  sortBy: ConversationsSortBy;
  readState: ConversationsReadState;
  period: ContactsLastInteraction;
}

export const DEFAULT_CONVERSATIONS_FILTERS: ConversationsFilters = {
  tab: 'all',
  search: '',
  subscriptionId: null,
  sortBy: 'recent',
  readState: 'all',
  period: 'any',
};

export const CONVERSATIONS_READ_STATE_OPTIONS: { value: ConversationsReadState; label: string }[] = [
  { value: 'all', label: 'Tous les échanges' },
  { value: 'unread', label: 'Non lus' },
  { value: 'read', label: 'Lus' },
];

// Same periods as the contacts, "any" renamed for the panel
export const CONVERSATIONS_PERIOD_OPTIONS: { value: ContactsLastInteraction; label: string }[] =
  CONTACTS_LAST_INTERACTION_OPTIONS.map((option) => (option.value === 'any' ? { ...option, label: 'Toute période' } : option));

export const CONVERSATIONS_TABS: { value: ConversationsTab; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'calls', label: 'Appels' },
  { value: 'sms', label: 'SMS' },
  { value: 'receptionist', label: 'Réceptionniste' },
];

export const CONVERSATIONS_SORT_OPTIONS: { value: ConversationsSortBy; label: string }[] = [
  { value: 'recent', label: 'Plus récentes' },
  { value: 'oldest', label: 'Plus anciennes' },
  { value: 'unread', label: 'Non lues d\'abord' },
];

// A receptionist call is also a call
const TAB_TYPES: Record<Exclude<ConversationsTab, 'all'>, ConversationActivityType[]> = {
  calls: ['call', 'receptionist'],
  sms: ['sms'],
  receptionist: ['receptionist'],
};

export interface ConversationDisplay {
  // A number can talk to several lines: one conversation per line
  key: string;
  phone: string;
  subscriptionId: string;
  fullName: string;
  initials: string;
  formattedPhone: string;
  followedBy: string;
  isOwn: boolean;
  typology: ContactTag | null;
  lastActivity: CompanyConversation['last_activity'];
  unreadCount: number;
  types: ConversationActivityType[];
  // The contact behind the number, null when the number is not saved
  contact: ContactDisplay | null;
}

export interface ConversationThreadDay {
  timestamp: number;
  items: ConversationThreadItem[];
}

export function getConversationKey(subscriptionId: string, phone: string): string {
  return `${subscriptionId}:${phone}`;
}

export function buildConversationDisplay(
  conversation: CompanyConversation,
  contactsById: Map<string, Omit<CompanyContact, 'subscription_id'>>,
  tagsById: Map<string, ContactTag>,
  subscriptions: ContactSubscription[],
  userNameBySubscription: Map<string, string>,
  now: Dayjs = dayjs(),
): ConversationDisplay {
  const contact = conversation.contact_id ? contactsById.get(conversation.contact_id) : undefined;
  // An unknown number goes through the same display as a contact with only its number
  const display = buildContactDisplay(
    contact
      ? { ...contact, subscription_id: conversation.subscription_id }
      : { id: '', subscription_id: conversation.subscription_id, created: 0, contact: { phone: conversation.phone } },
    tagsById,
    subscriptions,
    userNameBySubscription,
    now,
  );

  return {
    key: getConversationKey(conversation.subscription_id, conversation.phone),
    phone: conversation.phone,
    subscriptionId: conversation.subscription_id,
    fullName: display.fullName,
    initials: display.initials,
    formattedPhone: display.phone,
    followedBy: display.followedBy,
    isOwn: display.isOwn,
    typology: display.typologies[0] ?? null,
    lastActivity: conversation.last_activity,
    unreadCount: conversation.unread_count,
    types: conversation.types,
    contact: contact ? display : null,
  };
}

// A number already loaded can come back in an older page (a recent call, then older unread SMS): both are merged
export function mergeConversationPages(loaded: CompanyConversation[], page: CompanyConversation[]): CompanyConversation[] {
  const byKey = new Map(loaded.map((conversation) => [getConversationKey(conversation.subscription_id, conversation.phone), conversation]));
  page.forEach((conversation) => {
    const key = getConversationKey(conversation.subscription_id, conversation.phone);
    const previous = byKey.get(key);
    if (!previous) {
      byKey.set(key, conversation);
      return;
    }
    byKey.set(key, {
      ...previous,
      contact_id: previous.contact_id ?? conversation.contact_id,
      last_activity: conversation.last_activity.timestamp > previous.last_activity.timestamp ? conversation.last_activity : previous.last_activity,
      unread_count: previous.unread_count + conversation.unread_count,
      types: [...new Set([...previous.types, ...conversation.types])].sort(),
    });
  });
  return [...byKey.values()];
}

// Conversation opened from a link before its page is loaded: only its number and line are known
export function buildEmptyConversation(subscriptionId: string, phone: string): CompanyConversation {
  return {
    phone,
    subscription_id: subscriptionId,
    contact_id: null,
    last_activity: { type: 'sms', timestamp: 0, preview: null },
    unread_count: 0,
    types: [],
  };
}

// Preview of the last activity in the list
export function formatActivityPreview(activity: CompanyConversation['last_activity']): string {
  if (activity.type === 'sms') {
    const text = activity.preview ?? '';
    return activity.role === 'user' ? text : `Vous : ${text}`;
  }
  if (activity.preview) return activity.preview;
  if (activity.type === 'receptionist') return 'Réceptionniste a répondu';
  if (activity.missed) return 'Appel manqué';
  return activity.direction === 'outbound' ? 'Appel sortant' : 'Appel entrant';
}

export function formatCallDuration(seconds: number): string {
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  if (!minutes) return `${rest} s`;
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

export function formatCallClock(seconds: number): string {
  if (!seconds) return '';
  const rest = Math.round(seconds % 60);
  return `${Math.floor(seconds / 60)}:${String(rest).padStart(2, '0')}`;
}

export interface CallTranscriptMessage {
  speaker: string | null;
  // The user and the receptionist are on the right
  isUser: boolean;
  time: string | null;
  text: string;
}

export interface CallTranscriptContext {
  // `sender` of the call: the number of the Leedflow line
  linePhone: string;
  contactName: string | null;
  contactPhone: string;
}

type TranscriptSpeakerKind = 'user' | 'receptionist' | 'contact';

const SRT_TIMING = /^(\d+):(\d+):(\d+)(?:[,.]\d+)?\s*-->/;
const TRANSCRIPT_USER_ROLES = ['commercial', 'agent', 'support', 'conseiller', 'vendeur'];
const GENERIC_SPEAKER = /^speaker[_\-\s]?\d+$/i;
const SPEAKER_PLACEHOLDER = /:\w+_unknown:/g;

const normalizePhone = (value: string) => value.replace(/[\s+\-()]/g, '');

function speakerHasPhone(speaker: string, phone: string): boolean {
  if (!phone || phone === '-') return false;
  return speaker.includes(phone) || (normalizePhone(phone).length > 5 && normalizePhone(speaker).includes(normalizePhone(phone)));
}

function speakerDisplayName(speaker: string): string {
  const name = speaker.split(' - ')[0].trim();
  if (!name || GENERIC_SPEAKER.test(name)) return 'Interlocuteur';
  return name.replace(SPEAKER_PLACEHOLDER, '').trim() || 'Interlocuteur';
}

function speakerNamesMatch(a: string, b: string): boolean {
  const words = (value: string) =>
    new Set(
      value
        .toLowerCase()
        .replace(/[^a-zàâäéèêëïîôöùûüÿç\s]/g, ' ')
        .split(/\s+/)
        .filter((word) => word.length > 1),
    );
  const wordsA = words(a);
  const wordsB = words(b);
  if (wordsA.size === 0 || wordsB.size === 0) return false;
  return [...wordsA].filter((word) => wordsB.has(word)).length >= Math.min(wordsA.size, wordsB.size);
}

// Speaker labels: `[Agent IA]`, `[Prénom Nom - Agent - +336...]`, `[Vous - Agent - +336...]`, `[Correspondant - +336...]`
function classifySpeaker(speaker: string, context: CallTranscriptContext): TranscriptSpeakerKind | null {
  if (!speaker) return 'contact';
  if (speaker === 'Agent IA') return 'receptionist';
  const parts = speaker.split(' - ').map((part) => part.trim().toLowerCase());
  if (parts[0] === 'vous') return 'user';
  if (parts[0] === 'correspondant') return 'contact';
  if (parts.length >= 2 && TRANSCRIPT_USER_ROLES.includes(parts[1])) return 'user';
  if (speakerHasPhone(speaker, context.linePhone)) return 'user';
  if (speakerHasPhone(speaker, context.contactPhone)) return 'contact';
  if (context.contactName && speakerNamesMatch(speakerDisplayName(speaker), context.contactName)) return 'contact';
  return null;
}

// Port of parseSrtToMessages (mobile): who is who in the SRT, and the time of each block
export function parseCallTranscript(srt: string, context: CallTranscriptContext): CallTranscriptMessage[] {
  const blocks = srt
    .trim()
    .split(/\n\s*\n/)
    .flatMap((block) => {
      const lines = block.trim().split('\n');
      const timingIndex = lines.findIndex((line) => SRT_TIMING.test(line));
      if (timingIndex === -1) return [];
      const [, hours, minutes, seconds] = lines[timingIndex].match(SRT_TIMING) ?? [];
      const textLines = lines.slice(timingIndex + 1);
      const ownLine = textLines[0]?.match(/^\[(.+?)\]\s*$/);
      const inline = ownLine ? null : textLines[0]?.match(/^\[(.+?)\]\s*(.+)/);
      const speaker = (ownLine ?? inline)?.[1].trim() ?? '';
      const text = (ownLine ? textLines.slice(1) : inline ? [inline[2], ...textLines.slice(1)] : textLines).join(' ').trim();
      return text ? [{ speaker, time: `${Number(hours) * 60 + Number(minutes)}:${seconds}`, text }] : [];
    });

  if (blocks.length === 0) {
    const text = srt.trim();
    return text && text !== '-' ? [{ speaker: null, isUser: false, time: null, text }] : [];
  }

  const speakers = [...new Set(blocks.map((block) => block.speaker))];
  const kinds = new Map(speakers.map((speaker) => [speaker, classifySpeaker(speaker, context)]));
  // Two speakers, one known: the other one is the opposite side
  if (speakers.length === 2) {
    const [first, second] = speakers;
    const opposite = (kind: TranscriptSpeakerKind) => (kind === 'contact' ? 'user' : 'contact');
    const firstKind = kinds.get(first);
    const secondKind = kinds.get(second);
    if (firstKind && !secondKind) kinds.set(second, opposite(firstKind));
    if (secondKind && !firstKind) kinds.set(first, opposite(secondKind));
  }

  return blocks.map((block) => {
    const kind = kinds.get(block.speaker) ?? 'contact';
    const speaker =
      kind === 'user' ? 'Vous' : kind === 'receptionist' ? 'Donna' : (context.contactName ?? speakerDisplayName(block.speaker));
    return { speaker, isUser: kind !== 'contact', time: block.time, text: block.text };
  });
}

// Same rule as the mobile (getTaskViewForTimestamp): a todo task is late once its day is over
export function getCallTaskStatus(task: AiReceptionistCallTask, now: Dayjs = dayjs()): 'done' | 'overdue' | null {
  if (task.status === 'done') return 'done';
  if (task.status === 'todo' && task.timestamp < now.startOf('day').valueOf()) return 'overdue';
  return null;
}

function matchesTab(conversation: ConversationDisplay, tab: ConversationsTab): boolean {
  if (tab === 'all') return true;
  return conversation.types.some((type) => TAB_TYPES[tab].includes(type));
}

// Filters of the panel set apart from their defaults, counted on the "Filtres" button
export function countActiveFilters(filters: ConversationsFilters): number {
  return (['subscriptionId', 'readState', 'period'] as const).filter(
    (key) => filters[key] !== DEFAULT_CONVERSATIONS_FILTERS[key],
  ).length;
}

export function filterConversations(
  conversations: ConversationDisplay[],
  filters: ConversationsFilters,
  now: Dayjs = dayjs(),
): ConversationDisplay[] {
  const query = filters.search.trim().toLowerCase();
  const digitsQuery = query.replace(/[\s.+]/g, '');

  return conversations.filter((conversation) => {
    if (!matchesTab(conversation, filters.tab)) return false;
    if (filters.subscriptionId && conversation.subscriptionId !== filters.subscriptionId) return false;
    if (filters.readState === 'unread' && conversation.unreadCount === 0) return false;
    if (filters.readState === 'read' && conversation.unreadCount > 0) return false;
    if (!matchesLastInteraction(conversation.lastActivity.timestamp, filters.period, now)) return false;
    if (!query) return true;

    return (
      conversation.fullName.toLowerCase().includes(query) ||
      (!!digitsQuery && conversation.formattedPhone.replace(/[\s.+]/g, '').includes(digitsQuery))
    );
  });
}

// Oldest activity the period filter can show, null when it has no lower bound
function getPeriodStart(period: ConversationsFilters['period'], now: Dayjs): number | null {
  if (period === 'today') return now.startOf('day').valueOf();
  if (period === '7_days') return now.subtract(7, 'day').valueOf();
  if (period === '30_days') return now.subtract(30, 'day').valueOf();
  return null;
}

// Cursors of the lines whose next page can still show something with these filters, null when none
export function getPendingCursors(
  cursors: Record<string, number> | null,
  filters: ConversationsFilters,
  now: Dayjs = dayjs(),
): Record<string, number> | null {
  const periodStart = getPeriodStart(filters.period, now);
  const pending = Object.entries(cursors ?? {}).filter(
    ([subscriptionId, before]) =>
      (!filters.subscriptionId || subscriptionId === filters.subscriptionId) && (periodStart === null || before > periodStart),
  );
  return pending.length > 0 ? Object.fromEntries(pending) : null;
}

// Unread conversations of each tab, shown on the tab
export function countUnreadByTab(conversations: ConversationDisplay[]): Record<ConversationsTab, number> {
  const unread = conversations.filter((conversation) => conversation.unreadCount > 0);
  return {
    all: unread.length,
    calls: unread.filter((conversation) => matchesTab(conversation, 'calls')).length,
    sms: unread.filter((conversation) => matchesTab(conversation, 'sms')).length,
    receptionist: unread.filter((conversation) => matchesTab(conversation, 'receptionist')).length,
  };
}

export function sortConversations(conversations: ConversationDisplay[], sortBy: ConversationsSortBy): ConversationDisplay[] {
  const byRecent = [...conversations].sort((a, b) => b.lastActivity.timestamp - a.lastActivity.timestamp);
  if (sortBy === 'oldest') return byRecent.reverse();
  if (sortBy === 'unread') return byRecent.sort((a, b) => Number(b.unreadCount > 0) - Number(a.unreadCount > 0));
  return byRecent;
}

// Time of the last activity in the list: the hour today, "Hier", then the date
export function formatConversationTime(timestamp: number, now: Dayjs = dayjs()): string {
  const day = dayjs(timestamp);
  if (day.isSame(now, 'day')) return day.format('HH:mm');
  if (day.isSame(now.subtract(1, 'day'), 'day')) return 'Hier';
  return day.format(day.isSame(now, 'year') ? 'D MMM' : 'D MMM YYYY');
}

// Separator of the thread: "Aujourd'hui", "Hier", then the date
export function formatThreadDay(timestamp: number, now: Dayjs = dayjs()): string {
  return dayjs(timestamp).isSame(now, 'day') ? "Aujourd'hui" : formatConversationTime(timestamp, now);
}

export function groupThreadByDay(items: ConversationThreadItem[]): ConversationThreadDay[] {
  const days: ConversationThreadDay[] = [];
  items.forEach((item) => {
    const lastDay = days[days.length - 1];
    if (lastDay && dayjs(lastDay.timestamp).isSame(item.timestamp, 'day')) {
      lastDay.items.push(item);
    } else {
      days.push({ timestamp: item.timestamp, items: [item] });
    }
  });
  return days;
}
